import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  Bug, 
  Clock, 
  Activity,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Server,
  Globe,
  Database,
  User
} from 'lucide-react';
import { ErrorTrackingService, ErrorEvent } from '@/services/monitoring/errorTrackingService';
import { PerformanceCharts } from './PerformanceCharts';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const ErrorDashboard: React.FC = () => {
  const [errors, setErrors] = useState<ErrorEvent[]>([]);
  const [errorStats, setErrorStats] = useState<Record<string, number>>({});
  const [performanceMetrics, setPerformanceMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(24);

  useEffect(() => {
    loadErrorData();
  }, [timeRange]);

  const loadErrorData = async () => {
    setLoading(true);
    try {
      const [recentErrors, stats, metrics] = await Promise.all([
        ErrorTrackingService.getRecentErrors(100),
        ErrorTrackingService.getErrorStats(timeRange),
        ErrorTrackingService.getPerformanceMetrics(100)
      ]);
      
      setErrors(recentErrors);
      setErrorStats(stats);
      setPerformanceMetrics(metrics);
    } catch (error) {
      console.error('Error loading error data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'javascript': return Bug;
      case 'network': return Globe;
      case 'database': return Database;
      case 'user_action': return User;
      default: return AlertTriangle;
    }
  };

  const totalErrors = errors.length;
  const criticalErrors = errors.filter(e => e.severity === 'critical').length;
  const resolvedErrors = errors.filter(e => e.resolved).length;
  const errorRate = totalErrors > 0 ? ((totalErrors - resolvedErrors) / totalErrors * 100).toFixed(1) : '0';

  // Prepare chart data
  const errorsByHour = errors.reduce((acc, error) => {
    const hour = new Date(error.timestamp).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
    hour: `${hour}:00`,
    errors: errorsByHour[hour] || 0
  }));

  const errorsByType = Object.entries(
    errors.reduce((acc, error) => {
      acc[error.error_type] = (acc[error.error_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).map(([type, count]) => ({ type, count }));

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Feilsporing & Overvåking</h1>
          <p className="text-muted-foreground">Sanntidsovervåking av applikasjonsfeil og ytelse</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={loadErrorData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </Button>
          <Tabs value={timeRange.toString()} onValueChange={(value) => setTimeRange(parseInt(value))}>
            <TabsList>
              <TabsTrigger value="1">1t</TabsTrigger>
              <TabsTrigger value="24">24t</TabsTrigger>
              <TabsTrigger value="168">7d</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Alert for critical errors */}
      {criticalErrors > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Kritiske feil oppdaget!</AlertTitle>
          <AlertDescription>
            Det er {criticalErrors} kritiske feil som krever umiddelbar oppmerksomhet.
          </AlertDescription>
        </Alert>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale feil</CardTitle>
            <Bug className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalErrors}</div>
            <p className="text-xs text-muted-foreground">
              Siste {timeRange} timer
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kritiske feil</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{criticalErrors}</div>
            <p className="text-xs text-muted-foreground">
              Krever umiddelbar handling
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Feilrate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{errorRate}%</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {parseFloat(errorRate) > 5 ? (
                <TrendingUp className="h-3 w-3 mr-1 text-destructive" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1 text-success" />
              )}
              Uløste feil
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Løste feil</CardTitle>
            <RefreshCw className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{resolvedErrors}</div>
            <p className="text-xs text-muted-foreground">
              Av {totalErrors} totalt
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Details */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Trender</TabsTrigger>
          <TabsTrigger value="details">Feildetaljer</TabsTrigger>
          <TabsTrigger value="performance">Ytelse</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Hourly Error Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Feil per time</CardTitle>
                <CardDescription>Feilfrekvens over døgnet</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="errors" 
                      stroke="hsl(var(--destructive))" 
                      fill="hsl(var(--destructive))" 
                      fillOpacity={0.6} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Error Types */}
            <Card>
              <CardHeader>
                <CardTitle>Feiltyper</CardTitle>
                <CardDescription>Fordeling av ulike feiltyper</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={errorsByType}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Siste feil</CardTitle>
              <CardDescription>Detaljert oversikt over nylige feil</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {errors.slice(0, 20).map((error, index) => {
                  const IconComponent = getTypeIcon(error.error_type);
                  return (
                    <div key={index} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <IconComponent className="h-5 w-5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={getSeverityColor(error.severity) as any}>
                            {error.severity}
                          </Badge>
                          <Badge variant="outline">
                            {error.error_type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {new Date(error.timestamp).toLocaleString('no-NO')}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">
                          {error.error_message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {error.page_url}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <PerformanceCharts metrics={performanceMetrics} />
        </TabsContent>
      </Tabs>
    </div>
  );
};